import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

interface CommunityGuidelinesModalProps {
    isOpen: boolean;
    onAgree: () => Promise<void>;
}

const ModalBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-overlay);
  backdrop-filter: blur(8px);
  z-index: 10000;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md};
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  width: 100%;
  max-width: 500px;
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  max-height: 90vh;
  position: relative;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const RulesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  padding-right: ${theme.spacing.xs};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-medium);
    border-radius: 3px;
  }
`;

const RuleItem = styled.li`
  display: flex;
  gap: ${theme.spacing.md};
`;

const RuleIcon = styled.div`
  min-width: 24px;
  height: 24px;
  background: rgba(236, 100, 53, 0.1);
  color: ${theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
`;

const RuleText = styled.div`
  flex: 1;
`;

const RuleTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
`;

const RuleDescription = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  padding: ${theme.spacing.sm} 0;
  border-top: 1px solid var(--border-light);
  margin-top: ${theme.spacing.sm};
  user-select: none;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  accent-color: ${theme.colors.primary};
  margin-top: 2px;
`;

const AgreementText = styled.div`
  font-size: 0.9rem;
  color: var(--text-primary);
  line-height: 1.4;
`;

const ErrorText = styled.div`
  color: ${theme.colors.error};
  font-size: 0.9rem;
  text-align: center;
`;

const EnterButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    background: var(--bg-secondary);
    color: var(--text-disabled);
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    background: ${theme.colors.hover.primary};
    transform: translateY(-1px);
  }
`;

const RULES = [
    {
        title: 'Be Respectful',
        description: 'Treat everyone with kindness and respect. Harassment, hate speech, and bullying are not tolerated.'
    },
    {
        title: 'Keep it Safe',
        description: 'Do not share personal financial information, private addresses, or sensitive data in public chats.'
    },
    {
        title: 'No NSFW Content',
        description: 'This is a friendly community. Explicit, violent, or sexually suggestive content is strictly prohibited.'
    },
    {
        title: 'No Spam',
        description: 'Do not spam chat rooms with promotions, repetitive messages, or irrelevant links.'
    },
    {
        title: 'Report Issues',
        description: 'If you see behavior that violates these rules, please report it to our moderation team immediately.'
    }
];

export const CommunityGuidelinesModal: React.FC<CommunityGuidelinesModalProps> = ({
    isOpen,
    onAgree
}) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const handleEnter = async () => {
        setError(null);
        setIsSubmitting(true);
        try {
            await onAgree();
        } catch (err) {
            setError('Something went wrong saving your agreement. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalBackdrop $isOpen={isOpen}>
            <ModalContent>
                <Title>Community Guidelines</Title>
                <Subtitle>Please review and agree to our community rules to join the conversation.</Subtitle>

                <RulesList>
                    {RULES.map((rule, index) => (
                        <RuleItem key={index}>
                            <RuleIcon>{index + 1}</RuleIcon>
                            <RuleText>
                                <RuleTitle>{rule.title}</RuleTitle>
                                <RuleDescription>{rule.description}</RuleDescription>
                            </RuleText>
                        </RuleItem>
                    ))}
                </RulesList>

                <CheckboxContainer>
                    <Checkbox
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                    />
                    <AgreementText>
                        I have read and agree to follow the Community Rules and Guidelines. I understand that violating these rules may result in account suspension.
                    </AgreementText>
                </CheckboxContainer>

                {error && <ErrorText>{error}</ErrorText>}

                <EnterButton
                    disabled={!isChecked || isSubmitting}
                    onClick={handleEnter}
                >
                    {isSubmitting ? 'Saving...' : 'Enter Community'}
                </EnterButton>
            </ModalContent>
        </ModalBackdrop>
    );
};
